import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import './NewTaskDragGhost.scss'

export interface NewTaskDragGhostProps {
  /** Eine neue Aufgabe (#301) oder ein neuer Abschnitt (#314) — der trägt die Optik eines Kopfs, ohne Haken. */
  kind?: 'task' | 'section'
  width: number
  height: number
  /** Neigung in Grad — der Zug wählt die Richtung zufällig. */
  tilt: number
  /**
   * Linke obere Ecke auf dem Bildschirm. Fehlt sie, setzt der Zug sie selbst — bei jeder Bewegung
   * per Ref, nicht per Render (`useNewTaskDrag`).
   */
  at?: { left: number; top: number }
}

/**
 * **Die Fläche am Zeiger**, wenn „Neue Aufgabe“ aus der Listen-Werkzeug-Pille gezogen wird (#301):
 * eine markierte, leere Aufgabe in der Farbe des Lebensbereichs — Haken und blasser Text „Neue
 * Aufgabe“, so hoch wie die Lücke, die sie öffnet. Schmal (240px), mittig gegriffen, leicht geneigt
 * wie eine gezogene Aufgabe (`.drag-overlay-lift`).
 *
 * Liegt als Portal über allem und fängt keine Zeigerereignisse — darunter wird gezielt.
 *
 * @example
 * <NewTaskDragGhost ref={ghostRef} width={240} height={34} tilt={-1.5} />
 */
export const NewTaskDragGhost = React.forwardRef<HTMLDivElement, NewTaskDragGhostProps>(function NewTaskDragGhost(
  { kind = 'task', width, height, tilt, at },
  ref,
) {
  const { t } = useTranslation()
  const vars = {
    '--new-task-ghost-width': `${width}px`,
    '--new-task-ghost-height': `${height}px`,
    '--new-task-ghost-tilt': `${tilt}deg`,
    ...(at && { left: `${at.left}px`, top: `${at.top}px` }),
  } as React.CSSProperties

  return createPortal(
    <div ref={ref} className={`new-task-drag-ghost new-task-drag-ghost--${kind}`} style={vars} aria-hidden="true">
      {kind === 'task' && (
        <svg className="new-task-drag-ghost__check" width="16" height="16" viewBox="0 0 20 20" fill="none">
          <path
            d="M10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18Z"
            strokeWidth="1.33"
            strokeMiterlimit="10"
            strokeLinecap="square"
          />
        </svg>
      )}
      <span className="new-task-drag-ghost__label">{t(kind === 'section' ? 'project.addSection' : 'listToolbar.addTask')}</span>
    </div>,
    document.body,
  )
})
